import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  Grid,
} from "@mui/material";

const BookingForm = ({ handleClose, handleSubmit, initialData }) => {
  const [channels, setChannels] = useState([]);
  const [sources, setSources] = useState([]);

  // Remove duplicate useEffect and combine API fetches
  useEffect(() => {
    if (initialData) {
      const formattedData = {
        ...initialData,
        booking_date: initialData.booking_date
          ? new Date(initialData.booking_date).toISOString().split(".")[0]
          : new Date().toISOString().split(".")[0],
      };
      setFormData(formattedData);
    }

    const fetchData = async () => {
      try {
        // Fetch channels
        const channelResponse = await fetch(
          "http://localhost:8080/BookingSource",
        );
        if (channelResponse.ok) {
          const channelData = await channelResponse.json();
          if (channelData.success && Array.isArray(channelData.data)) {
            setChannels(channelData.data);
          }
        }

        // Fetch sources
        const sourceResponse = await fetch(
          "http://localhost:8080/DistributionChannel",
        );
        if (sourceResponse.ok) {
          const sourceData = await sourceResponse.json();
          if (sourceData.success && Array.isArray(sourceData.data)) {
            setSources(sourceData.data);
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [initialData]);

  const [formData, setFormData] = useState({
    booking_id: "",
    source_id: "",
    channel_id: "",
    reservation_id: "",
    guest_email: "",
    guest_phone: "",
    total_paid: 0.0,
    commission_amount: 0.0,
    currency: "INR",
    exchange_rate: 1.0,
    booking_date: new Date().toISOString().split(".")[0],
  });

  const currencies = ["INR", "USD", "EUR", "GBP"];

  useEffect(() => {
    const fetchChannels = async () => {
      try {
        const response = await fetch(
          "https://api.esthell.com/DistributionChannel",
        );
        if (response.ok) {
          const data = await response.json();
          if (data.success && Array.isArray(data.data)) {
            setChannels(data.data);
          }
        }
      } catch (error) {
        console.error("Error fetching channels:", error);
      }
    };

    const fetchSources = async () => {
      try {
        const response = await fetch("https://api.esthell.com/BookingSource");
        if (response.ok) {
          const data = await response.json();
          if (data.success && Array.isArray(data.data)) {
            setSources(data.data);
          }
        }
      } catch (error) {
        console.error("Error fetching sources:", error);
      }
    };

    fetchChannels();
    fetchSources();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const onSubmit = (e) => {
    e.preventDefault();
    handleSubmit(formData);
  };

  return (
    <Box
      component="form"
      onSubmit={onSubmit}
      sx={{ p: 2, bgcolor: "background.paper", borderRadius: 2, boxShadow: 1 }}
    >
      <Typography
        variant="h6"
        sx={{
          fontSize: 16,
          fontWeight: "600",
          color: "#000",
          fontFamily: "Public Sans",
          mb: 2,
        }}
      >
        {initialData ? "Update Booking" : "New Booking"}
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            required
            fullWidth
            size="small"
            name="booking_id"
            label="Booking ID"
            type="number"
            value={formData.booking_id}
            onChange={handleChange}
            sx={{ "& .MuiInputBase-root": { height: "35px" } }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            required
            fullWidth
            size="small"
            name="source_id"
            label="Source ID"
            type="number"
            value={formData.source_id}
            onChange={handleChange}
            sx={{ "& .MuiInputBase-root": { height: "35px" } }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            required
            fullWidth
            size="small"
            name="reservation_id"
            label="Reservation ID"
            value={formData.reservation_id}
            onChange={handleChange}
            sx={{ "& .MuiInputBase-root": { height: "35px" } }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            required
            fullWidth
            size="small"
            select
            name="channel_id"
            label="Channel Name"
            value={formData.channel_id}
            onChange={handleChange}
            sx={{ "& .MuiInputBase-root": { height: "35px" } }}
          >
            <MenuItem value="">
              <em>Select Channel</em>
            </MenuItem>
            {channels.map((channel) => (
              <MenuItem key={channel.id} value={channel.channel_id}>
                {channel.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            required
            fullWidth
            size="small"
            name="guest_email"
            label="Guest Email"
            type="email"
            value={formData.guest_email}
            onChange={handleChange}
            sx={{ "& .MuiInputBase-root": { height: "35px" } }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            required
            fullWidth
            size="small"
            name="guest_phone"
            label="Guest Phone"
            value={formData.guest_phone}
            onChange={handleChange}
            sx={{ "& .MuiInputBase-root": { height: "35px" } }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            required
            fullWidth
            size="small"
            name="total_paid"
            label="Total Paid"
            type="number"
            value={formData.total_paid}
            onChange={handleChange}
            inputProps={{ step: "0.01", min: "0" }}
            sx={{ "& .MuiInputBase-root": { height: "35px" } }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            required
            fullWidth
            size="small"
            name="commission_amount"
            label="Commission Amount"
            type="number"
            value={formData.commission_amount}
            onChange={handleChange}
            inputProps={{ step: "0.01", min: "0" }}
            sx={{ "& .MuiInputBase-root": { height: "35px" } }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            required
            fullWidth
            size="small"
            select
            name="currency"
            label="Currency"
            value={formData.currency}
            onChange={handleChange}
            sx={{ "& .MuiInputBase-root": { height: "35px" } }}
          >
            {currencies.map((currency) => (
              <MenuItem key={currency} value={currency}>
                {currency}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            required
            fullWidth
            size="small"
            name="booking_date"
            label="Booking Date"
            type="datetime-local"
            value={formData.booking_date}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
            sx={{ "& .MuiInputBase-root": { height: "35px" } }}
          />
        </Grid>
      </Grid>

      <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 2 }}>
        <Button onClick={handleClose} variant="outlined">
          Cancel
        </Button>
        <Button type="submit" variant="contained" color="primary">
          {initialData ? "Update" : "Save"}
        </Button>
      </Box>
    </Box>
  );
};

export default BookingForm;
